/**
 * Registrations - FR-3.1.3's pending queue (spec 03's routes, spec 12's first
 * screen).
 *
 * The queue is only ever `pending`: every other status is a filter on Members. A
 * decision here is final in the sense that these three routes all refuse a second
 * one (409 `registration_already_decided`) - after that the member is managed from
 * their profile.
 *
 * `request-info` requires remarks and the other two do not, which is FR-3.1.5: a
 * member asked for information needs to be told what for. The dialog enforces that
 * client-side only to avoid a pointless round trip; the server refuses it
 * regardless (`errors.remarks_required`).
 *
 * Approval has a second effect worth knowing: `account_service.change_status`
 * writes the `user_status_events` row, the audit entry AND the
 * `registration_decision` notification in one transaction. So the member learns
 * the decision by push and in their app inbox without anything else being done
 * here.
 */

import { useState } from "react";

import { DataTable, PrimaryCell, type Column } from "../components/DataTable";
import {
  Badge,
  Button,
  Card,
  ErrorNotice,
  EmptyState,
  Field,
  LoadingRows,
  Modal,
  Page,
  Pagination,
  SuccessNotice,
  Textarea,
  statusTone,
} from "../components/primitives";
import {
  approveRegistration,
  fetchRegistrationQueue,
  rejectRegistration,
  requestRegistrationInfo,
  type RegistrationQueueItem,
} from "../lib/api/accounts";
import { useMutation, useQuery } from "../lib/hooks";
import type { Page as PageType } from "../lib/api/types";
import { formatDate, humanizeStatus, relativeDay } from "../lib/format";

const PAGE_SIZE = 50;

type Decision = "approve" | "reject" | "request-info";

const DECISION_COPY: Record<
  Decision,
  { title: string; body: string; confirm: string; needsRemarks: boolean; done: string }
> = {
  approve: {
    title: "Approve this registration?",
    body: "The member becomes active. They can sign in and use the app as soon as they have a valid membership.",
    confirm: "Approve",
    needsRemarks: false,
    done: "Registration approved. The member can now sign in.",
  },
  reject: {
    title: "Turn down this registration?",
    body: "The member is set to rejected and told the decision. Remarks, if you add them, are shown to the member.",
    confirm: "Reject",
    needsRemarks: false,
    done: "Registration turned down. The member has been told.",
  },
  "request-info": {
    title: "Ask for more information",
    body: "The registration stays pending. The member is told what you need; tell them clearly, because this is the only message they get.",
    confirm: "Send request",
    needsRemarks: true,
    done: "Request sent. The registration stays pending until they reply.",
  },
};

export function RegistrationsScreen() {
  const [offset, setOffset] = useState(0);
  const [decision, setDecision] = useState<{ member: RegistrationQueueItem; kind: Decision } | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const query = useQuery<PageType<RegistrationQueueItem>>(
    () => fetchRegistrationQueue(PAGE_SIZE, offset),
    [offset],
  );
  const { data, error, loading, refreshing, reload } = query;

  const columns: Array<Column<RegistrationQueueItem>> = [
    {
      key: "member",
      header: "Applicant",
      render: (row) => (
        <PrimaryCell
          title={row.full_name}
          detail={row.email ?? "No email given"}
        />
      ),
    },
    {
      key: "mobile",
      header: "Mobile",
      render: (row) => <span className="mono">{row.mobile_number}</span>,
    },
    {
      key: "requested",
      header: "Requested",
      render: (row) => (
        <PrimaryCell title={formatDate(row.created_at)} detail={relativeDay(row.created_at)} />
      ),
    },
    {
      key: "last_event",
      header: "Last activity",
      render: (row) =>
        row.last_event ? (
          <PrimaryCell
            title={humanizeStatus(row.last_event.to_status)}
            detail={row.last_event.remarks ?? formatDate(row.last_event.created_at)}
          />
        ) : (
          <span className="text-meta">No activity yet</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <Badge tone={statusTone(row.status)}>{row.status}</Badge>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div className="table__actions">
          <Button
            size="sm"
            variant="accent"
            onClick={() => setDecision({ member: row, kind: "approve" })}
          >
            Approve
          </Button>
          <Button
            size="sm"
            onClick={() => setDecision({ member: row, kind: "request-info" })}
          >
            Ask
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => setDecision({ member: row, kind: "reject" })}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Page>
      {saved ? <SuccessNotice message={saved} onDismiss={() => setSaved(null)} /> : null}
      {error ? <ErrorNotice error={error} onRetry={reload} /> : null}

      <Card flush>
        <div style={{ padding: "24px 24px 16px" }}>
          <div className="row row--between">
            <div>
              <h2 className="card__title">Waiting for a decision</h2>
              <p className="card__hint">
                {data
                  ? data.total === 0
                    ? "Nobody is waiting."
                    : `${data.total} registration${data.total === 1 ? "" : "s"} waiting.`
                  : "Loading…"}
              </p>
            </div>
            <Button size="sm" onClick={reload} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </div>

        {loading && !data ? (
          <LoadingRows rows={5} columns={6} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No registrations waiting."
            body="New registration requests appear here as soon as they are submitted."
          />
        ) : (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(row) => row.id} />
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              noun="registrations"
              onChange={setOffset}
            />
          </>
        )}
      </Card>

      {decision ? (
        <DecisionDialog
          member={decision.member}
          kind={decision.kind}
          onClose={() => setDecision(null)}
          onDone={(message) => {
            setDecision(null);
            setSaved(message);
            reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function DecisionDialog({
  member,
  kind,
  onClose,
  onDone,
}: {
  member: RegistrationQueueItem;
  kind: Decision;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const copy = DECISION_COPY[kind];
  const [remarks, setRemarks] = useState("");

  const mutation = useMutation(
    (text: string) => {
      if (kind === "approve") return approveRegistration(member.id, text || undefined);
      if (kind === "reject") return rejectRegistration(member.id, text || undefined);
      return requestRegistrationInfo(member.id, text);
    },
    {
      // These three routes return the member with no `message`, so the
      // confirmation is this screen's own sentence - phrased to claim only what
      // the route did.
      onSuccess: () => onDone(copy.done),
    },
  );

  const blocked = copy.needsRemarks && remarks.trim() === "";

  return (
    <Modal
      title={copy.title}
      description={
        <>
          <strong>{member.full_name}</strong> · {member.mobile_number}
          <br />
          {copy.body}
        </>
      }
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 16 }}>
        <Field
          label={copy.needsRemarks ? "What do you need? (required)" : "Remarks (optional)"}
          hint={copy.needsRemarks ? undefined : "Shown to the member with the decision."}
        >
          {(id) => (
            <Textarea
              id={id}
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
              maxLength={1000}
              disabled={mutation.pending}
            />
          )}
        </Field>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button
            variant={kind === "reject" ? "danger" : "accent"}
            disabled={mutation.pending || blocked}
            onClick={() => void mutation.run(remarks.trim())}
          >
            {mutation.pending ? "Working…" : copy.confirm}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
